export interface CodeRowInput {
  code: string;
  description: string;
}

/**
 * One operator-entered code row, possibly representing multiple actual CPTs:
 * - "97140"             -> codes: ["97140"], isExpansion: false
 * - "97010-97035"       -> codes: ["97010"..."97035"], isExpansion: true
 * - "29240, 29530, ..." -> codes: ["29240", ...], isExpansion: true
 */
export interface CodeGroup {
  /** Original input string ("97010-97035", "97140", "29240, 29530, 29540"). */
  source: string;
  description: string;
  codes: string[];
  isExpansion: boolean;
}

const RANGE_RE = /^\s*(\d+)\s*-\s*(\d+)\s*$/;

const parseRange = (raw: string): string[] | null => {
  const m = RANGE_RE.exec(raw);
  if (!m) return null;
  const startStr = m[1];
  const endStr = m[2];
  const start = parseInt(startStr, 10);
  const end = parseInt(endStr, 10);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  const width = startStr.length;
  return Array.from({ length: end - start + 1 }, (_, i) =>
    String(start + i).padStart(width, '0'),
  );
};

const parseCommaList = (raw: string): string[] | null => {
  if (!raw.includes(',')) return null;
  const parts = raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : null;
};

export const expandCodeRow = (input: CodeRowInput): CodeGroup => {
  const raw = input.code.trim();
  const rangeCodes = parseRange(raw);
  if (rangeCodes) {
    return {
      source: raw,
      description: input.description,
      codes: rangeCodes,
      isExpansion: rangeCodes.length > 1,
    };
  }
  const listCodes = parseCommaList(raw);
  if (listCodes) {
    return {
      source: raw,
      description: input.description,
      codes: listCodes,
      isExpansion: listCodes.length > 1,
    };
  }
  return {
    source: raw,
    description: input.description,
    codes: [raw],
    isExpansion: false,
  };
};

export const expandCodeRows = (rows: CodeRowInput[]): CodeGroup[] =>
  rows.map(expandCodeRow);

/** Flat list of unique CPT strings across all groups, preserving first-seen order. */
export const flattenCodes = (groups: CodeGroup[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const g of groups) {
    for (const c of g.codes) {
      if (seen.has(c)) continue;
      seen.add(c);
      out.push(c);
    }
  }
  return out;
};
